import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ChipProps, SelectChangeEvent } from '@mui/material';
import { useId, useState, type ReactNode } from 'react';

import { SessionTelemetryCategory, SessionTelemetryOutcome } from '../../graphql/__generated__/graphql';
import {
  getSessionTelemetryAudioStateLabel,
  getSessionTelemetryCategoryLabel,
  getSessionTelemetryConnectivityLabel,
  getSessionTelemetryEventTypeLabel,
  formatSessionTelemetryDetails,
  getSessionTelemetryGpsStateLabel,
  getSessionTelemetryOutcomeLabel,
  getSessionTelemetrySourceLabel,
} from './canvassingSessionLabels';
import { CanvassingSessionTimestamp } from './CanvassingSessionTimestamp';
import {
  CANVASSING_SESSION_TELEMETRY_PAGE_SIZE_OPTIONS,
  type CanvassingSessionFetchImpl,
} from './canvassingSessionApi';
import { useCanvassingSessionTelemetry } from './useCanvassingSessionTelemetry';

export type CanvassingSessionTelemetrySectionProps = {
  sessionId: string;
  pollingRecommended: boolean;
  fetchImpl?: CanvassingSessionFetchImpl;
};

type TelemetryMetricProps = {
  label: string;
  value: ReactNode;
};

const TelemetryMetric = ({ label, value }: TelemetryMetricProps) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="dt">
      {label}
    </Typography>
    <Typography variant="body2" component="dd" sx={{ m: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  </Box>
);

const categoryFromSelectValue = (value: string): SessionTelemetryCategory | undefined =>
  Object.values(SessionTelemetryCategory).find(category => category === value);

const getOutcomeColor = (outcome: SessionTelemetryOutcome): ChipProps['color'] => {
  if (outcome === SessionTelemetryOutcome.Failure) return 'error';
  if (outcome === SessionTelemetryOutcome.Success) return 'success';
  return 'info';
};

const hasErrorData = (item: {
  errorName?: string | null;
  errorMessage?: string | null;
  stackTrace?: string | null;
  errorTruncated?: boolean;
}): boolean => item.errorTruncated || item.errorName != null || item.errorMessage != null || item.stackTrace != null;

export const CanvassingSessionTelemetrySection = ({
  sessionId,
  pollingRecommended,
  fetchImpl,
}: CanvassingSessionTelemetrySectionProps) => {
  const headingId = useId();
  const [expandedEventIds, setExpandedEventIds] = useState<ReadonlySet<string>>(new Set());
  const telemetry = useCanvassingSessionTelemetry({ sessionId, pollingRecommended, fetchImpl });
  const { controls, state } = telemetry;

  const handleCategoryChange = (event: SelectChangeEvent<string>): void => {
    telemetry.setCategory(categoryFromSelectValue(event.target.value));
  };

  const toggleEventDetails = (eventId: string): void => {
    setExpandedEventIds(current => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const readyPublication = state.status === 'ready' ? state.publication : null;
  const latestHeartbeat = readyPublication?.summary.latestHeartbeat ?? null;
  const pageCount = readyPublication === null ? 1 : Math.max(1, Math.ceil(readyPublication.total / controls.rowsPerPage));

  return (
    <Paper component="section" variant="outlined" aria-labelledby={headingId} sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography id={headingId} variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
            Mobile Telemetry
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Device telemetry is an authenticated diagnostic timeline. Client occurrence and server receipt times are shown separately.
          </Typography>
        </Box>

        <Stack
          data-testid="mobile-telemetry-controls"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap' }}
        >
          <FormControl fullWidth size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
            <InputLabel id={`${headingId}-category-label`}>Category</InputLabel>
            <Select
              labelId={`${headingId}-category-label`}
              id={`${headingId}-category`}
              value={controls.category ?? ''}
              label="Category"
              onChange={handleCategoryChange}
              disabled={state.status === 'loading' || state.status === 'ready' && state.refreshing}
              inputProps={{ 'aria-label': 'Telemetry category' }}
            >
              <MenuItem value="">All categories</MenuItem>
              {Object.values(SessionTelemetryCategory).map(category => (
                <MenuItem key={category} value={category}>
                  {getSessionTelemetryCategoryLabel(category)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ width: { xs: '100%', sm: 'auto' }, m: 0 }}
            control={
              <Switch
                checked={controls.failuresOnly}
                onChange={event => telemetry.setFailuresOnly(event.target.checked)}
                disabled={state.status === 'loading' || state.status === 'ready' && state.refreshing}
              />
            }
            label="Failures only"
          />
          <FormControl fullWidth size="small" sx={{ width: { xs: '100%', sm: 180 } }}>
            <InputLabel id={`${headingId}-page-size-label`}>Rows per page</InputLabel>
            <Select
              labelId={`${headingId}-page-size-label`}
              id={`${headingId}-page-size`}
              value={controls.rowsPerPage}
              label="Rows per page"
              onChange={event => telemetry.setRowsPerPage(Number(event.target.value))}
              disabled={state.status === 'loading' || state.status === 'ready' && state.refreshing}
              inputProps={{ 'aria-label': 'Mobile telemetry rows per page' }}
            >
              {CANVASSING_SESSION_TELEMETRY_PAGE_SIZE_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={telemetry.reset}
            disabled={state.status === 'loading' || state.status === 'ready' && state.refreshing}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Reset filters
          </Button>
          <Button
            variant="outlined"
            startIcon={state.status === 'ready' && state.refreshing ? <CircularProgress size={16} aria-hidden /> : <RefreshIcon />}
            onClick={telemetry.refresh}
            disabled={state.status === 'loading' || state.status === 'ready' && state.refreshing}
            aria-busy={state.status === 'ready' && state.refreshing}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Refresh Mobile Telemetry
          </Button>
        </Stack>

        {state.status === 'loading' && (
          <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <CircularProgress size={18} aria-hidden />
            <Typography variant="body2" color="text.secondary">
              {state.manualRefreshing ? 'Refreshing mobile telemetry…' : 'Loading mobile telemetry…'}
            </Typography>
          </Stack>
        )}

        {state.status === 'unavailable' && (
          <Alert severity="info">Mobile telemetry is unavailable for this session.</Alert>
        )}

        {state.status === 'error' && (
          <Alert
            severity={state.error.kind === 'forbidden' ? 'warning' : 'error'}
            action={<Button color="inherit" size="small" onClick={telemetry.retry}>Retry telemetry</Button>}
          >
            {state.error.kind === 'forbidden' ? 'Permission required. ' : ''}{state.error.message}
          </Alert>
        )}

        {state.status === 'ready' && readyPublication !== null && (
          <>
            {state.manualRefreshing && (
              <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <CircularProgress size={18} aria-hidden />
                <Typography variant="body2" color="text.secondary">Refreshing mobile telemetry…</Typography>
              </Stack>
            )}
            {state.backgroundError !== null && (
              <Alert
              severity="warning"
                action={<Button color="inherit" size="small" onClick={telemetry.retry}>Retry telemetry</Button>}
              >
                {state.backgroundError.kind === 'forbidden'
                  ? `Permission required. ${state.backgroundError.message}`
                  : "Couldn't refresh mobile telemetry. Displayed data may be stale."}
              </Alert>
            )}

            <Grid container spacing={2} component="dl" sx={{ m: 0 }} aria-label="Latest telemetry heartbeat summary">
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <TelemetryMetric label="Telemetry events" value={readyPublication.summary.total} />
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <TelemetryMetric label="Latest server receipt" value={<CanvassingSessionTimestamp timestamp={readyPublication.summary.latestReceivedAt} />} />
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <TelemetryMetric label="Latest heartbeat occurrence" value={<CanvassingSessionTimestamp timestamp={latestHeartbeat?.occurredAt} />} />
                  <TelemetryMetric label="Latest heartbeat receipt" value={<CanvassingSessionTimestamp timestamp={latestHeartbeat?.receivedAt} />} />
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  {latestHeartbeat === null ? (
                    <Typography variant="body2" color="text.secondary">No heartbeat received.</Typography>
                  ) : (
                    <Stack component="dl" spacing={0.75} sx={{ m: 0 }}>
                      <TelemetryMetric label="Audio" value={getSessionTelemetryAudioStateLabel(latestHeartbeat.audioState)} />
                      <TelemetryMetric label="GPS" value={getSessionTelemetryGpsStateLabel(latestHeartbeat.gpsState)} />
                      <TelemetryMetric label="Connectivity" value={getSessionTelemetryConnectivityLabel(latestHeartbeat.connectivity)} />
                      <TelemetryMetric label="Confirmed uploads" value={latestHeartbeat.confirmedUploadCount} />
                      <TelemetryMetric label="Pending uploads" value={latestHeartbeat.pendingUploadCount} />
                      <TelemetryMetric label="Last segment captured" value={<CanvassingSessionTimestamp timestamp={latestHeartbeat.lastSegmentCapturedAt} />} />
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {readyPublication.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No mobile telemetry events match the selected filters.</Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1500 }} aria-label="Mobile Telemetry timeline">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sequence</TableCell>
                      <TableCell>Client occurrence</TableCell>
                      <TableCell>Server receipt</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Event type</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {readyPublication.items.map(item => {
                      const expanded = expandedEventIds.has(item.eventId);
                      const detailsId = `telemetry-details-${item.eventId}`;
                      const rowHasErrorData = hasErrorData(item);
                      const eventDetails = formatSessionTelemetryDetails(item.details);
                      const rowHasDetails = rowHasErrorData || eventDetails !== '';
                      return (
                        <TableRow key={item.eventId} hover>
                          <TableCell>{item.sequence}</TableCell>
                          <TableCell><CanvassingSessionTimestamp timestamp={item.occurredAt} /></TableCell>
                          <TableCell><CanvassingSessionTimestamp timestamp={item.receivedAt} /></TableCell>
                          <TableCell>{getSessionTelemetrySourceLabel(item.source)}</TableCell>
                          <TableCell sx={{ maxWidth: 260, overflowWrap: 'anywhere' }}>{getSessionTelemetryEventTypeLabel(item.type)}</TableCell>
                          <TableCell>
                            <Chip
                              label={getSessionTelemetryOutcomeLabel(item.outcome)}
                              color={getOutcomeColor(item.outcome)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {rowHasDetails ? (
                              <>
                                <Button
                                  size="small"
                                  onClick={() => toggleEventDetails(item.eventId)}
                                  aria-label={`${expanded ? 'Hide' : 'Show'} details for telemetry event ${item.sequence}`}
                                  aria-expanded={expanded}
                                  aria-controls={detailsId}
                                >
                                  {expanded ? 'Hide details' : 'Show details'}
                                </Button>
                                {expanded && (
                                  <Box id={detailsId} component="section" aria-label={`Details for telemetry event ${item.sequence}`} sx={{ mt: 1, minWidth: 320 }}>
                                    <Stack spacing={1}>
                                      {rowHasErrorData && (
                                        <>
                                          <Typography variant="body2"><strong>Error name:</strong> {item.errorName ?? 'Not available'}</Typography>
                                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}><strong>Error message:</strong> {item.errorMessage ?? 'Not available'}</Typography>
                                          {item.errorTruncated && <Chip label="Stored error was truncated" color="warning" size="small" variant="outlined" />}
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">Stored stack trace</Typography>
                                            <Box
                                              component="pre"
                                              tabIndex={0}
                                              aria-label={`Stored stack trace for telemetry event ${item.sequence}`}
                                              sx={{
                                                m: 0,
                                                mt: 0.5,
                                                p: 1,
                                                maxHeight: 240,
                                                overflow: 'auto',
                                                whiteSpace: 'pre-wrap',
                                                overflowWrap: 'anywhere',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                bgcolor: 'background.default',
                                              }}
                                            >
                                              {item.stackTrace ?? 'No stack trace was stored.'}
                                            </Box>
                                          </Box>
                                        </>
                                      )}
                                      {eventDetails !== '' && (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Event details</Typography>
                                          <Box
                                            component="pre"
                                            tabIndex={0}
                                            aria-label={`Event details for telemetry event ${item.sequence}`}
                                            sx={{
                                              m: 0,
                                              mt: 0.5,
                                              p: 1,
                                              maxHeight: 240,
                                              overflow: 'auto',
                                              whiteSpace: 'pre-wrap',
                                              overflowWrap: 'anywhere',
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              bgcolor: 'background.default',
                                            }}
                                          >
                                            {eventDetails}
                                          </Box>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Box>
                                )}
                              </>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {readyPublication.items.length} of {readyPublication.total} telemetry events.
              </Typography>
              <Pagination
                count={pageCount}
                page={controls.page + 1}
                onChange={(_event, nextPage) => telemetry.setPage(nextPage - 1)}
                disabled={state.refreshing}
                showFirstButton
                showLastButton
                shape="rounded"
                size="small"
                aria-label="Mobile telemetry pagination"
              />
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};
